import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { AdminAuthProvider } from "@/lib/admin-auth";
import { ProtectedRoute, AdminProtectedRoute } from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import PublicBooking from "@/pages/PublicBooking";
import BookNow from "@/pages/BookNow";
import Approve from "@/pages/Approve";
import QuoteApprove from "@/pages/QuoteApprove";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import SubscriptionPolicy from "@/pages/SubscriptionPolicy";
import BillingSuccess from "@/pages/billing/Success";
import BillingCancelled from "@/pages/billing/Cancelled";

import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import JobsList from "@/pages/jobs/JobsList";
import JobNew from "@/pages/jobs/JobNew";
import JobDetail from "@/pages/jobs/JobDetail";
import CheckIn from "@/pages/jobs/CheckIn";
import Handover from "@/pages/jobs/Handover";
import CalendarPage from "@/pages/Calendar";
import BookingNew from "@/pages/BookingNew";
import Money from "@/pages/Money";
import More from "@/pages/More";
import CustomersList from "@/pages/customers/CustomersList";
import CustomerNew from "@/pages/customers/CustomerNew";
import CustomerDetail from "@/pages/customers/CustomerDetail";
import VehiclesList from "@/pages/vehicles/VehiclesList";
import VehicleNew from "@/pages/vehicles/VehicleNew";
import VehicleDetail from "@/pages/vehicles/VehicleDetail";
import QuotationsList from "@/pages/quotations/QuotationsList";
import QuotationNew from "@/pages/quotations/QuotationNew";
import QuotationEdit from "@/pages/quotations/QuotationEdit";
import QuotationDetail from "@/pages/quotations/QuotationDetail";
import InvoiceDetail from "@/pages/invoice/InvoiceDetail";
import InvoiceNew from "@/pages/invoice/InvoiceNew";
import Parts from "@/pages/Parts";
import Suppliers from "@/pages/Suppliers";
import Team from "@/pages/Team";
import MotLookup from "@/pages/MotLookup";
import Billing from "@/pages/Billing";
import BusinessSettings from "@/pages/BusinessSettings";
import InvoiceDesign from "@/pages/InvoiceDesign";
import OnlineBookingSetup from "@/pages/OnlineBookingSetup";
import Gdpr from "@/pages/Gdpr";


import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminGarage from "@/pages/admin/AdminGarage";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminPromoters from "@/pages/admin/AdminPromoters";
import OwnerSetup from "@/pages/admin/OwnerSetup";

const P = ({ children }) => <ProtectedRoute>{children}</ProtectedRoute>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AdminAuthProvider>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/b/:token" element={<PublicBooking />} />
              <Route path="/book/:token" element={<BookNow />} />
              <Route path="/approve/:token" element={<Approve />} />
              <Route path="/quote/:token" element={<QuoteApprove />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/subscription-policy" element={<SubscriptionPolicy />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancelled" element={<BillingCancelled />} />

              {/* Protected app */}
              <Route path="/dashboard" element={<P><Dashboard /></P>} />
              <Route path="/onboarding" element={<P><Onboarding /></P>} />
              <Route path="/jobs" element={<P><JobsList /></P>} />
              <Route path="/jobs/new" element={<P><JobNew /></P>} />
              <Route path="/jobs/check-in" element={<P><CheckIn /></P>} />
              <Route path="/jobs/handover" element={<P><Handover /></P>} />
              <Route path="/jobs/:id" element={<P><JobDetail /></P>} />
              <Route path="/calendar" element={<P><CalendarPage /></P>} />
              <Route path="/booking-new" element={<P><BookingNew /></P>} />
              <Route path="/money" element={<P><Money /></P>} />
              <Route path="/more" element={<P><More /></P>} />
              <Route path="/customers" element={<P><CustomersList /></P>} />
              <Route path="/customers/new" element={<P><CustomerNew /></P>} />
              <Route path="/customers/:id" element={<P><CustomerDetail /></P>} />
              <Route path="/vehicles" element={<P><VehiclesList /></P>} />
              <Route path="/vehicles/new" element={<P><VehicleNew /></P>} />
              <Route path="/vehicles/:id" element={<P><VehicleDetail /></P>} />
              <Route path="/quotations" element={<P><QuotationsList /></P>} />
              <Route path="/quotation-new" element={<P><QuotationNew /></P>} />
              <Route path="/quotations/:id" element={<P><QuotationDetail /></P>} />
              <Route path="/quotations/:id/edit" element={<P><QuotationEdit /></P>} />
              <Route path="/invoice/new" element={<P><InvoiceNew /></P>} />
              <Route path="/invoice/:id" element={<P><InvoiceDetail /></P>} />
              <Route path="/parts" element={<P><Parts /></P>} />
              <Route path="/suppliers" element={<P><Suppliers /></P>} />
              <Route path="/team" element={<P><Team /></P>} />
              <Route path="/mot-lookup" element={<P><MotLookup /></P>} />
              <Route path="/billing" element={<P><Billing /></P>} />
              <Route path="/business-settings" element={<P><BusinessSettings /></P>} />
              <Route path="/invoice-design" element={<P><InvoiceDesign /></P>} />
              <Route path="/online-booking" element={<P><OnlineBookingSetup /></P>} />
              <Route path="/gdpr" element={<P><Gdpr /></P>} />

              {/* Super Admin / Platform Owner */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/garage/:id" element={<AdminProtectedRoute><AdminGarage /></AdminProtectedRoute>} />
              {/* /owner canonical routes (alias, same protected components) */}
              <Route path="/owner/setup" element={<OwnerSetup />} />
              <Route path="/owner/login" element={<AdminLogin />} />
              <Route path="/owner" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/owner/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/owner/garage/:id" element={<AdminProtectedRoute><AdminGarage /></AdminProtectedRoute>} />
              <Route path="/owner/promotions" element={<AdminProtectedRoute><AdminPromotions /></AdminProtectedRoute>} />
              <Route path="/owner/promoters" element={<AdminProtectedRoute><AdminPromoters /></AdminProtectedRoute>} />
              <Route path="/admin/promotions" element={<AdminProtectedRoute><AdminPromotions /></AdminProtectedRoute>} />
              <Route path="/admin/promoters" element={<AdminProtectedRoute><AdminPromoters /></AdminProtectedRoute>} />
            </Routes>
            <Toaster position="top-right" richColors />
          </AdminAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
